import { useCallback } from 'react'

import { FaqSettingMinimal, FilterSearchOnlyLayout, SearchParams } from '@karakuri-web/faq-type'

import { FilterSearch } from '../components'
import { useFaqRouting } from '../contexts/FaqRoutingContext'
import {
  type UseFilterSearchOptions,
  useCompleteLazy,
  useFilterSearch,
  usePlaceholder,
  useSuggestLazy,
} from '../hooks'

interface FilterSearchOnlyWidgetSettings extends FaqSettingMinimal {
  waitMs?: {
    complete?: number | null
  } | null
  useAutoSearch?: boolean | null
  useKnowledgeSearch?: boolean | null
  words?: {
    search?: {
      placeholder?: string | null
      message?: string | null
      keyword?: string | null
      showKeyword?: string | null
      hideKeyword?: string | null
    } | null
  } | null
}

interface PageProps<D, C, S> {
  data: D
  pageContext?: C
  path: string
  location: { pathname: string }
  state?: S
}

type Props<T extends FilterSearchOnlyWidgetSettings = FilterSearchOnlyWidgetSettings> = Pick<
  PageProps<{ settingYaml: T }, Record<string, unknown>, SearchParams>,
  'data' | 'location'
> & {
  layout: FilterSearchOnlyLayout
  q: string[]
  onSearch?: (q: string[], page?: number) => void
}

const getPathname = (location: { pathname: string }) => {
  return location.pathname === '/' ? '/search' : location.pathname.replace(/\/*$/, '')
}

/**
 * 絞り込み検索（検索欄のみ）ウィジェット
 */
export function FilterSearchOnlyWidget<
  T extends FilterSearchOnlyWidgetSettings = FilterSearchOnlyWidgetSettings,
>({ layout, data, location, q, onSearch }: Props<T>) {
  const { navigate } = useFaqRouting()
  const [complete, { data: completeResult }] = useCompleteLazy()
  const [suggest, { data: suggestResult }] = useSuggestLazy()

  const callComplete = useCallback<UseFilterSearchOptions['callComplete']>(
    ({ variables: { input } }) => {
      void complete({ ...input, botId: input.botId || '' })
    },
    [complete]
  )
  const callSuggest = useCallback<UseFilterSearchOptions['callSuggest']>(
    ({ variables: { input } }) => {
      void suggest({ ...input, botId: input.botId || '' })
    },
    [suggest]
  )

  const { options, keywords, handleInput, handleChange, handleSelect, handleSearch } =
    useFilterSearch({
      botId: data.settingYaml.botId,
      tenantId: data.settingYaml.tenantId,
      q,
      waitMs: data.settingYaml.waitMs?.complete || 500,
      useAutoSearch: data.settingYaml.useAutoSearch || false,
      useKnowledgeSearch: data.settingYaml.useKnowledgeSearch || false,
      location,
      onSearch,
      callComplete,
      callSuggest,
      completeResult,
      suggestResult,
      navigate,
      getPathname,
    })

  const placeholder = usePlaceholder(data.settingYaml?.words?.search?.placeholder || '', q)

  return (
    // 絞り込み検索のsuggestがtenant毎に切り替わるようにkeyを設定
    <form
      onSubmit={handleSearch}
      key={data.settingYaml.tenantId}
      className="faq-filter-search-only-widget"
      data-testid="faq-filter-search-only-form"
    >
      <div className="faq-panel faq-filter-search">
        <h3 className="faq-panel-title">{layout.title || 'キーワード検索'}</h3>
        <FilterSearch
          title={data.settingYaml?.words?.search?.message || ''}
          onSearch={handleSearch}
          keywordProps={{
            keywords,
            title: data.settingYaml?.words?.search?.keyword || '',
            showKeywordButtonLabel: data.settingYaml?.words?.search?.showKeyword || '',
            hideKeywordButtonLabel: data.settingYaml?.words?.search?.hideKeyword || '',
            onSelect: handleSelect,
          }}
          searchProps={{
            value: q,
            placeholder,
            options,
            onInput: handleInput,
            onChange: handleChange,
          }}
        />
      </div>
    </form>
  )
}
